import React from 'react';
import { Box, Button, HStack, Icon, VStack } from '@chakra-ui/react';
import { LuArrowUp, LuListPlus } from 'react-icons/lu';
import ComposerAttachActions from './ComposerAttachActions.tsx';
import ComposerPrivacyToggle from './ComposerPrivacyToggle.tsx';
import ComposerTaskScheduleControls from './ComposerTaskScheduleControls.tsx';
import { RecordComposerController } from './useRecordComposer.ts';
import { TaskComposerController } from './useTaskComposer.ts';
import { getSlideInCss } from '@/components/ui/motion.ts';
import {
	COMPOSER_ACTION_ROW_SLIDE_MS,
	COMPOSER_ACTION_ROW_SUBMIT_SLIDE_DISTANCE_PX,
	COMPOSER_ATTACH_SLIDE_PX,
	COMPOSER_BAR_CONTROL_H,
	COMPOSER_ACTION_ROW_CONTAINER_CSS,
} from './composerConstants.ts';
import { COMPOSER_ACTION_PILL_PROPS } from './composerActionButtonStyles.ts';
import { COMPOSER_SUBMIT_KEYSHORTCUTS } from './composerSubmitShortcut.ts';

interface ComposerActionRowProps {
	isRecordTab: boolean;
	isTaskTab?: boolean;
	record: RecordComposerController;
	task: TaskComposerController;
	submitLabel: string;
	canSubmit: boolean;
	isSubmitting: boolean;
	onSubmit: () => void;
	onAddTaskChild?: () => void;
	onToggleTaskRecurring?: () => void;
	/** Slide the controls in from the sides on appear (desktop inline bar; the sheet leaves it off). */
	animateIn?: boolean;
}

/**
 * The add-mode action row: attach + privacy (Records only) on the left, the
 * per-tab submit pill on the right. Shared by the desktop inline bar and the
 * mobile add sheet so the two stay in lockstep.
 */
const ComposerActionRow: React.FC<ComposerActionRowProps> = (props) => {
	const {
		isRecordTab,
		isTaskTab = !isRecordTab,
		record,
		task,
		submitLabel,
		canSubmit,
		isSubmitting,
		onSubmit,
		onAddTaskChild,
		onToggleTaskRecurring,
		animateIn,
	} = props;

	// Entrance only. An element can't slide from the edge it already sits against — it'd start
	// outside the bar. The attach group sits left, so it sweeps IN from the right (a long ~500px
	// travel across the bar at default size, staying inside). The submit group sits right, so it
	// comes from the left. They move toward opposite edges — spreading out from the bar's centre
	// rather than flying in from outside it.
	// Attach and submit distances are separate composer constants so each side can be tuned
	// independently without changing the generic motion helper default.
	const slideAttach = animateIn
		? getSlideInCss('right', COMPOSER_ACTION_ROW_SLIDE_MS, COMPOSER_ATTACH_SLIDE_PX)
		: undefined;
	const slideSubmit = animateIn
		? getSlideInCss('left', COMPOSER_ACTION_ROW_SLIDE_MS, COMPOSER_ACTION_ROW_SUBMIT_SLIDE_DISTANCE_PX)
		: undefined;

	return (
		<HStack
			gap={2}
			px={2}
			py={2}
			w="100%"
			align="flex-end"
			css={{ ...COMPOSER_ACTION_ROW_CONTAINER_CSS, '@media (max-width: 430px)': { paddingInline: '4px' } }}
		>
			{/* Controls row + frequency row nest together; the submit (outside) pins to the bottom. */}
			<VStack flex={1} minW={0} align="stretch" gap={0}>
				{isRecordTab ? (
					<HStack gap={2} w="100%" minW={0} align="center" css={slideAttach}>
						<Box flex={1} minW={0}>
							<ComposerAttachActions
								onFilesSelected={record.submitFiles}
								disabled={record.isSubmitting}
							/>
						</Box>
						<ComposerPrivacyToggle
							isPrivate={record.isPrivate}
							onToggle={record.togglePrivate}
							disabled={record.isSubmitting}
						/>
					</HStack>
				) : isTaskTab ? (
					<ComposerTaskScheduleControls
						task={task}
						title={task.title}
						controlsCss={slideAttach}
						onToggleRecurring={onToggleTaskRecurring}
					/>
				) : null}
			</VStack>

			<HStack
				alignSelf="flex-end"
				flexShrink={0}
				gap={2}
				css={{
					...slideSubmit,
					'@media (max-width: 430px)': { gap: '4px' },
				}}
			>
				{isTaskTab && task.childCreationEnabled && onAddTaskChild ? (
					<Button
						{...COMPOSER_ACTION_PILL_PROPS}
						aria-label="Add sub-task"
						boxSize={COMPOSER_BAR_CONTROL_H}
						minW={0}
						px={0}
						aspectRatio="1"
						onClick={onAddTaskChild}
						disabled={task.isSubmitting}
					>
						<Icon as={LuListPlus} boxSize={4} />
					</Button>
				) : null}
				<Button
					type="button"
					onClick={onSubmit}
					loading={isSubmitting}
					disabled={!canSubmit}
					aria-label={submitLabel}
					aria-keyshortcuts={COMPOSER_SUBMIT_KEYSHORTCUTS}
					rounded="full"
					boxSize={COMPOSER_BAR_CONTROL_H}
					minW={COMPOSER_BAR_CONTROL_H}
					px={0}
					bg="brand.solid"
					color="brand.contrast"
					flexShrink={0}
					_disabled={{ opacity: 0.5, cursor: 'default' }}
				>
					<Icon as={LuArrowUp} boxSize={5} />
				</Button>
			</HStack>
		</HStack>
	);
};

export default ComposerActionRow;
